import qs from "query-string";
import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";

type RolloutsBuilder = EndpointBuilder<BaseQueryFn, "OtaFiles" | "Rollouts", "rolloutsApi">;

const pageIndexOffset = 1;

type GetOtaFilesParams = {
    codename: string;
    workspaceId: number;
    includeMajorVersions?: number[];
    sortCriteria?: string;
    sortDirection?: "ascending" | "descending";
    pageIndex?: number;
    pageSize?: number;
    channels?: string[];
    statuses?: string[];
};

function getOtaFiles(builder: RolloutsBuilder) {
    return builder.query<PaginatedResponse<API.OtaFile>, GetOtaFilesParams>({
        providesTags: ["OtaFiles"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToFetchOtaFiles")),
        query: async ({ codename, pageIndex, includeMajorVersions = [], ...params }: GetOtaFilesParams) => {
            const base = await RouteService.getProductRoute();
            const c = encodeURIComponent(codename);
            const normalizedParams = {
                ...params,
                includeMajorVersions: includeMajorVersions,
                pageIndex: (pageIndex ?? 0) + pageIndexOffset,
            };
            return {
                url: `${base}updates/${c}`,
                params: normalizedParams,
                paramsSerializer: () => qs.stringify(normalizedParams),
            };
        },
    });
}

type DeleteOtaAnnotationPayload = {
    codename: string;
    version: string;
    workspaceId: number;
};

function deleteOtaAnnotation(builder: RolloutsBuilder) {
    return builder.mutation<void, DeleteOtaAnnotationPayload>({
        query: async ({ codename, version, workspaceId }) => {
            const base = await RouteService.getProductRoute();
            const c = encodeURIComponent(codename);
            const v = encodeURIComponent(version);
            return {
                url: `${base}annotations/${c}/${v}`,
                method: "DELETE",
                params: {
                    workspaceId,
                },
            };
        },
        invalidatesTags: invalidateOnSuccess("OtaFiles"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToDeleteOtaTags")),
    });
}

type SaveOtaAnnotationPayload = {
    codename: string;
    version: string;
    workspaceId: number;
    metadata: Record<string, string>;
};

function saveOtaAnnotation(builder: RolloutsBuilder) {
    return builder.mutation<void, SaveOtaAnnotationPayload>({
        query: async ({ codename, version, workspaceId, metadata }) => {
            const base = await RouteService.getProductRoute();
            const c = encodeURIComponent(codename);
            const v = encodeURIComponent(version);
            return {
                url: `${base}annotations/${c}/${v}/metadata`,
                method: "PATCH",
                data: {
                    workspaceId,
                    metadata,
                },
            };
        },
        invalidatesTags: invalidateOnSuccess("OtaFiles"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToSaveOtaTags")),
    });
}

export type CreateRolloutPayload = {
    codename: string;
    buildVersion: string;
    payload: {
        workspaceId: number;
        deviceIds: number[];
        installWindowStart?: string;
        installWindowEnd?: string;
        downloadWindowStart?: string;
        downloadWindowEnd?: string;
        options?: object | null;
    };
};

function createRollout(builder: RolloutsBuilder) {
    return builder.mutation<ApiResponse<API.Rollout>, CreateRolloutPayload>({
        invalidatesTags: invalidateOnSuccess("Rollouts"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToCreateRollout")),
        query: async ({ codename, buildVersion, payload }) => {
            const base = await RouteService.getProductRoute();
            const c = encodeURIComponent(codename);
            const v = encodeURIComponent(buildVersion);
            return {
                url: `${base}rollouts/${c}/${v}`,
                method: "POST",
                data: payload,
            };
        },
    });
}

type RolloutBuildParams = {
    codename: string;
    workspaceId: number;
    status?: string;
    sortBy?: string;
    sortDescending?: boolean;
    pageIndex?: number;
    pageSize?: number;
    includeMajorVersions?: number[];
};

function getRolloutsByBuild(builder: RolloutsBuilder) {
    return builder.query<PaginatedResponse<API.Rollout>, RolloutBuildParams>({
        providesTags: ["Rollouts"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToFetchRollout")),
        query: async ({ codename, workspaceId, status, sortBy, sortDescending, pageIndex, pageSize, includeMajorVersions }) => {
            const base = await RouteService.getProductRoute();
            const c = encodeURIComponent(codename);
            const params = {
                WorkspaceId: workspaceId,
                Status: status,
                SortBy: sortBy,
                SortDescending: sortDescending,
                PageIndex: pageIndex,
                PageSize: pageSize,
                IncludeMajorVersions: includeMajorVersions ?? [],
            };
            return {
                url: `${base}rollouts/${c}`,
                params,
                paramsSerializer: () => qs.stringify(params),
            };
        },
    });
}

type RolloutIdParam = {
    id: number;
};

function getRolloutById(builder: RolloutsBuilder) {
    return builder.query<ApiResponse<API.Rollout>, RolloutIdParam>({
        providesTags: ["Rollouts"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToFetchRollout")),
        query: async ({ id }) => {
            const base = await RouteService.getProductRoute();
            return {
                url: `${base}rollouts/${id}`,
            };
        },
    });
}

function deleteRollout(builder: RolloutsBuilder) {
    return builder.mutation<BasicApiResponse, RolloutIdParam>({
        invalidatesTags: invalidateOnSuccess("Rollouts"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToDeleteRollout")),
        query: async ({ id }) => {
            const base = await RouteService.getProductRoute();
            return {
                url: `${base}rollouts/${id}`,
                method: "DELETE",
            };
        },
    });
}

type UpdateRolloutPayload = RolloutIdParam & {
    payload: {
        cancelled?: boolean | null;
    };
};

function updateRollout(builder: RolloutsBuilder) {
    return builder.mutation<ApiResponse<API.Rollout>, UpdateRolloutPayload>({
        invalidatesTags: invalidateOnSuccess("Rollouts"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.rollouts.failedToUpdateRollout")),
        query: async ({ id, payload }) => {
            const base = await RouteService.getProductRoute();
            return {
                url: `${base}rollouts/${id}`,
                method: "PATCH",
                data: payload,
            };
        },
    });
}

export default {
    createRollout,
    deleteRollout,
    deleteOtaAnnotation,
    getOtaFiles,
    getRolloutsByBuild,
    getRolloutById,
    saveOtaAnnotation,
    updateRollout,
};
